import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Lock, Menu, X } from 'lucide-react';
import { ThemeToggle } from './ThemeToggle';
import { Button } from './ui/button';
import { PremiumLogo } from './PremiumLogo';

interface NavbarProps {
  showMobileMenu?: boolean;
  rightContent?: React.ReactNode;
  onMenuClick?: () => void;
}

const navLinks = [
  { href: '/', label: 'Home' },
  { href: '/about', label: 'About' },
  { href: '/faq', label: 'FAQ' },
  { href: '/contact', label: 'Contact' },
];

export const Navbar = ({ showMobileMenu = true, rightContent, onMenuClick }: NavbarProps) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="sticky top-0 z-50 glass-strong border-b border-border/40">
      <div className="container mx-auto px-4 h-16 flex items-center justify-between">
        {/* Left Side - Logo and Menu Button */}
        <div className="flex items-center gap-3">
          {showMobileMenu && onMenuClick && (
            <Button variant="ghost" size="icon" className="lg:hidden" onClick={onMenuClick}>
              <Menu className="w-5 h-5" />
            </Button>
          )}
          
          {/* Premium Logo */}
          <PremiumLogo size="md" showTagline={true} />
        </div>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-2">
          {navLinks.map((link, index) => (
            <Link
              key={link.href}
              to={link.href}
              className={`px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 hover-lift ${
                isActive(link.href)
                  ? 'gradient-bg text-primary-foreground shadow-glow'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary/80 hover:shadow-md'
              }`}
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Right Side */}
        <div className="flex items-center gap-4">
          {rightContent}
          
          <div className="hidden md:flex items-center gap-2 px-4 py-2 rounded-full bg-success/15 border border-success/30 hover-glow animate-fade-in">
            <Lock className="w-4 h-4 text-success animate-pulse" />
            <span className="text-xs font-semibold text-success">Encrypted</span>
          </div>
          
          <ThemeToggle />

          {/* Mobile Nav Menu Button */}
          {showMobileMenu && !onMenuClick && (
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          )}
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden glass-strong border-t border-border/40 animate-fade-in">
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-3 rounded-xl text-sm font-medium transition-colors ${
                  isActive(link.href)
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
};